import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  alpha,
  Alert,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import axiosInstance from '../../../lib/axios';

interface Supplier {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
}

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

interface QuickSupplierModalProps {
  open: boolean;
  onClose: () => void;
  onSupplierCreated: (supplier: Supplier) => void;
  colors: POSColors;
}

interface FormErrors {
  name?: string;
  code?: string;
}

/**
 * Generate a supplier code from the name
 * Pattern: SUPP-{first 3 chars of name}-{random suffix}
 */
const generateSupplierCode = (name: string): string => {
  if (!name.trim()) return '';
  const namePrefix = name
    .trim()
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, 3)
    .toUpperCase();
  const suffix = Date.now().toString(36).slice(-3).toUpperCase();
  return `SUPP-${namePrefix}-${suffix}`;
};

export const QuickSupplierModal: React.FC<QuickSupplierModalProps> = ({
  open,
  onClose,
  onSupplierCreated,
  colors,
}) => {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setName('');
      setCode('');
      setErrors({});
      setApiError(null);
      setIsLoading(false);
    }
  }, [open]);

  // Auto-generate code when name changes
  const handleNameChange = (value: string) => {
    setName(value);
    // Only auto-generate if code hasn't been manually edited or is empty
    if (!code || code === generateSupplierCode(name)) {
      setCode(generateSupplierCode(value));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Supplier name is required';
    }

    if (!code.trim()) {
      newErrors.code = 'Supplier code is required';
    } else {
      // Validate code format: uppercase, numbers, and dashes only
      const codeRegex = /^[A-Z0-9-]+$/;
      if (!codeRegex.test(code)) {
        newErrors.code = 'Code must contain only uppercase letters, numbers, and dashes';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await axiosInstance.post('/suppliers', {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        isActive: true,
      });

      const newSupplier = response.data;
      onSupplierCreated(newSupplier);
      onClose();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Failed to create supplier';
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            backgroundColor: alpha(colors.accent, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LocalShippingIcon sx={{ color: colors.accent, fontSize: 20 }} />
        </Box>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: colors.textPrimary,
          }}
        >
          Create Supplier
        </Typography>
      </DialogTitle>

      <DialogContent>
        {apiError && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: alpha(colors.accent, 0.1),
              color: colors.textPrimary,
              '& .MuiAlert-icon': {
                color: '#EF4444',
              },
            }}
          >
            {apiError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Supplier Name"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            size="small"
            required
            autoFocus
            sx={getTextFieldStyles(colors)}
          />

          <TextField
            label="Supplier Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            error={!!errors.code}
            helperText={errors.code || 'Auto-generated from name'}
            placeholder="SUPP-ABC-123"
            fullWidth
            size="small"
            required
            sx={getTextFieldStyles(colors)}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isLoading}
          sx={{
            borderColor: colors.border,
            color: colors.textSecondary,
            '&:hover': {
              borderColor: colors.textMuted,
              backgroundColor: alpha(colors.textMuted, 0.1),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: colors.accent,
            '&:hover': {
              backgroundColor: colors.accentLight,
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(colors.accent, 0.5),
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: '#FFF' }} />
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper function for text field styles
const getTextFieldStyles = (colors: POSColors) => ({
  '& .MuiInputLabel-root': {
    color: colors.textMuted,
    '&.Mui-focused': {
      color: colors.accent,
    },
    '&.Mui-error': {
      color: '#EF4444',
    },
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.surfaceLight,
    '& fieldset': {
      borderColor: colors.border,
    },
    '&:hover fieldset': {
      borderColor: colors.textMuted,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.accent,
    },
    '&.Mui-error fieldset': {
      borderColor: '#EF4444',
    },
  },
  '& .MuiOutlinedInput-input': {
    color: colors.textPrimary,
  },
  '& .MuiFormHelperText-root': {
    color: colors.textMuted,
    '&.Mui-error': {
      color: '#EF4444',
    },
  },
});

export default QuickSupplierModal;
